import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import { useSurveyParams } from '../context/SurveyParamsContext'
import 'survey-core/i18n/traditional-chinese'
import {
  convertVisibleIf,
  convertTriggers,
  convertQuestionMappings,
  q,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = null
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)
  // 名單式調查參數
  const { sfdSamplerId, sfdPId } = useSurveyParams()

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  const visibleIfConditionsInit = convertVisibleIf({
    // Q20(q20) 選 (1)全職工作、(2)兼職／自由工作 → 才顯示 Q21
    q21: '{q20} anyof [1, 2]',
  })
  const notRequired = null
  const mappedRateChoice = null
  const pageBreakQuestions = null
  // Q1(q1) 選 (4)以上時間皆不方便、Q2(q2) 選 (2)女性 → 問卷結束
  const triggersConditionsInit = convertTriggers([
    {
      type: 'complete',
      expression: `({q1} anyof ["none"]) or ({q2} = 2)`,
    },
  ])
  // Q13 有選到的選項 → 成為 Q14 選項
  const questionMappings = convertQuestionMappings({
    q13: 'q14',
  })

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
    sfdSamplerId,
    sfdPId,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    const model = new Model(convertedSurvey)
    // Q14 前三名車款：複選至多 3 項（後台 limit.max 未設）
    const question = model.getQuestionByName(q(14))
    if (question) question.maxSelectedChoices = 3
    return model
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
    sfdSamplerId,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  // 客製結束語（篩出與正常完成共用同一段）
  const endSurveyMsg = (survey) => {
    setCompletionMessage(
      `<div>感謝您的填答～我們已經收到您的報名資料囉！<br>若您符合本次活動之初步條件，我們會後續致電與您聯繫，進一步確認相關資料。<br>近期請留意陌生來電，以免錯過參加活動的機會。<br>感謝您的耐心等候，祝您有美好的一天！<br><br>若有調查相關問題，請洽典通市場調查公司<br>典通客服專線<br><span style="color: #00b0f0">02-23315133#836 葉小姐</span><br><span style="color: #00b0f0">問卷主題：＊北部車主團體座談會</span></div>`
    )
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    userIdRef,
    enableProgressSaving: webAuthType > 0 || !!sfdSamplerId,
    webAuthType,
    sfdSamplerId,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
